import { useForm } from 'react-hook-form'
import { Language } from '../services/adminService'
import { Button } from './ui/Button'
import { useTranslation } from '../i18n/hooks/useTranslation'

interface LanguageFormProps {
  language?: Language | null
  onSubmit: (data: Omit<Language, 'id' | 'createdAt' | 'updatedAt'>) => void
  onCancel: () => void
  isLoading: boolean
}

export function LanguageForm({ language, onSubmit, onCancel, isLoading }: LanguageFormProps) {
  const { t } = useTranslation()
  const { register, handleSubmit, watch, formState: { errors } } = useForm<Omit<Language, 'id' | 'createdAt' | 'updatedAt'>>({
    defaultValues: {
      code: language?.code || '',
      name: language?.name || '',
      nativeName: language?.nativeName || '',
      flag: language?.flag || '',
      isActive: language?.isActive ?? true,
      order: language?.order || 0,
    }
  })

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {language ? t('languages.editLanguage') : t('buttons.addLanguage')}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('languages.fields.language')} *
            </label>
            <input
              type="text"
              {...register('code', { 
                required: t('languages.errors.codeRequired'),
                pattern: {
                  value: /^[a-z]{2,3}$/,
                  message: t('languages.errors.codeFormat')
                }
              })}
              placeholder="en"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.code && (
              <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {t('languages.help.codeDescription')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('translations.fields.name')} *
            </label>
            <input
              type="text"
              {...register('name', { required: t('languages.errors.nameRequired') })}
              placeholder="English"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('translations.fields.name')} ({t('translations.languages.en')}) *
            </label>
            <input
              type="text"
              {...register('nativeName', { required: t('languages.errors.nativeNameRequired') })}
              placeholder="English"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.nativeName && (
              <p className="text-red-500 text-sm mt-1">{errors.nativeName.message}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {t('languages.help.nativeNameDescription')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('categories.fields.icon')} *
            </label>
            <input
              type="text"
              {...register('flag', { 
                required: t('languages.errors.flagRequired'),
                pattern: {
                  value: /^[\u{1F1E6}-\u{1F1FF}]{2}$/u,
                  message: t('languages.errors.flagFormat')
                }
              })}
              placeholder="🇺🇸"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.flag && (
              <p className="text-red-500 text-sm mt-1">{errors.flag.message}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {t('languages.help.flagDescription')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('categories.fields.order')}
            </label>
            <input
              type="number"
              {...register('order', { 
                required: t('languages.errors.orderRequired'), 
                valueAsNumber: true,
                min: { value: 0, message: t('languages.errors.orderMin') }
              })}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {errors.order && (
              <p className="text-red-500 text-sm mt-1">{errors.order.message}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {t('languages.help.orderDescription')}
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register('isActive')}
              id="isActive"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
              {t('common.active')}
            </label>
            <p className="ml-2 text-gray-500 text-xs">
              {t('languages.activeLanguagesNote')}
            </p>
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">{t('languages.help.preview')}</h4>
            <div className="text-sm text-blue-800">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{watch('flag') || '🏳️'}</span>
                <span className="font-medium">{watch('name') || 'Language Name'}</span>
                <span className="text-gray-600">({watch('nativeName') || 'Native Name'})</span>
                <span className="text-xs bg-blue-100 px-2 py-1 rounded uppercase font-mono">
                  {watch('code') || 'CODE'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
          >
            {language ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </form>
    </div>
  )
} 